/**
 * Raw API response (camelCase) - matches backend Dictionary serialization.
 */
export interface OrgRawEmployee {
  id: string;
  tenNhanVien?: string;
  maNhanVien?: string;
  bpid?: string;
  cvid?: string;
  email?: string;
  prefix?: string;
  hinh?: string;
  gioiTinh?: boolean;
  phanHe?: number | string;
  isQuanLy?: boolean;
}

export interface OrgRawDepartment {
  id: string;
  ten?: string;
  parentID?: string;
  viTri?: number;
  treePath?: string;
  key?: number;
  prefix?: string;
}

export interface OrgRawPosition {
  id: string;
  ten?: string;
  parentID?: string;
  viTri?: number;
}

export interface OrgRawUnit {
  id: string;
  ten?: string;
  childs?: any[];
  isDefault?: boolean;
}

export interface OrgRawRole {
  id: string;
  ten?: string;
  fullControl?: boolean;
  quanLy?: boolean;
  siteModule?: number;
}

export interface OrgRawPeriodDetail {
  id?: string;
  namBSCID?: string;
  year?: number;
  batDau?: string;
  ketThuc?: string;
  site?: string;
}

export interface OrgRawPeriod {
  id?: string;
  ten?: string;
  batDau?: string;
  ketThuc?: string;
  namTaiChinh?: number | string;
  site?: string;
  periodDetails?: OrgRawPeriodDetail[];
}

export interface OrgRawPerspective {
  id: string;
  ten?: string;
  mauNen?: string;
  mauChu?: string;
  mauSpan?: string;
  site?: string;
}

/** Raw organization payload from API (decrypted JSON). */
export interface OrgRawResponse {
  employees?: Record<string, OrgRawEmployee>;
  departments?: Record<string, OrgRawDepartment>;
  positions?: Record<string, OrgRawPosition>;
  units?: Record<string, OrgRawUnit>;
  roles?: Record<string, OrgRawRole>;
  period?: Record<string, OrgRawPeriod> | OrgRawPeriod[];
  perspectives?: Record<string, OrgRawPerspective>;
}

/**
 * Normalized entities (PascalCase) - stored in IndexedDB and used in app.
 */
export interface OrgEmployee {
ID: string;
TenNhanVien: string;
MaNhanVien?: string;
  BPID: string;
  CVID: string;
  Email?: string;
  Prefix?: string;
  Hinh?: string;
  GioiTinh?: boolean;
  NghiViec?: boolean;
  PhanHe?: number | string;
  IsQuanLy?: boolean;
  Parent?: string;
}

export interface OrgDepartment {
  ID: string;
  Ten: string;
  ParentID: string;
  ViTri: number;
  TreePath: string;
  Key: number | string;
  IsCompany: boolean;
  Prefix: string;
}

export interface OrgPosition {
  ID: string;
  Ten: string;
  ParentID: string;
  ViTri: number;
}

export interface OrgUnit {
  ID: string;
  Ten: string;
  Childs: any[];
  IsDefault: boolean;
  HinhThucTinh?: number | null;
  hinhThucTinh?: number | null;
}

export interface OrgRole {
  ID: string;
  Ten: string;
  FullControl: boolean;
  QuanLy: boolean;
  SiteModule: number;
}

export interface OrgPeriodDetail {
  ID: string;
  NamBSCID?: string;
  Year?: number;
  BatDau?: string;
  KetThuc?: string;
  Site?: string;
}

export interface OrgPeriod {
  ID: string;
  Ten?: string;
  BatDau?: string;
  KetThuc?: string;
  NamTaiChinh?: number | string;
  Site?: string;
  PeriodDetails: OrgPeriodDetail[];
}

export interface OrgPerspective {
  ID: string;
  Ten?: string;
  MauNen: string;
  MauChu: string;
  MauSpan: string;
  Site: string;
}

/**
 * Organization data stored in IndexedDB (normalized format).
 */
export interface OrganizationData {
  id: string;
  employees: OrgEmployee[];
  departments: OrgDepartment[];
  positions?: OrgPosition[];
  units: OrgUnit[];
  roles: OrgRole[];
  perspectives?: OrgPerspective[];
  period: OrgPeriod[];
  updatedAt: number;
  version?: string | null;
}

/** Entity type for organization tree (departments, positions). */
export type OrganizationEntityType = 'departments' | 'positions';

/** Version object từ API – gửi lại trong request để chỉ nhận data đổi. */
export interface OrgVersion {
  EmployeeVersion?: string;
  DepartmentVersion?: string;
  PositionVersion?: string;
  UnitVersion?: string;
  RoleVersion?: string;
  PeriodVersion?: string;
  PerspectiveVersion?: string;
}

/** IndexedDB: 4 key lưu tách employees, departments, positions, rest. */
export const ORG_KEY_EMPLOYEES = 'org_employees' as const;
export const ORG_KEY_DEPARTMENTS = 'org_departments' as const;
export const ORG_KEY_POSITIONS = 'org_positions' as const;
export const ORG_KEY_REST = 'org_rest' as const;

/** @deprecated Dùng 4 key ORG_KEY_* thay vì 1 key. Giữ để tương thích clear cache cũ. */
export const ORG_INDEXED_DB_ID = 'toppion_org_indexed_db_id' as const;
